import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { config } from "../config";

interface AccessLog {
  user_email: string;
  ip: string;
  user_agent: string;
  path: string;
  method: string;
  created_at: string;
}

function Admin() {
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [filterEmail, setFilterEmail] = useState('');

  useEffect(() => {
    fetchAccessLogs();
  }, [limit]);

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.apiBase}/access-logs?limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        setAccessLogs(data);
      } else {
        setError('Failed to fetch access logs. Check if authentication is working and the API is reachable.');
      }
    } catch (err) {
      setError('Failed to fetch access logs. Check if authentication is working and the API is reachable.');
      console.error('Access logs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = accessLogs.filter(log => 
    filterEmail === '' || log.user_email.toLowerCase().includes(filterEmail.toLowerCase())
  );

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPathIcon = (path: string) => {
    if (path.includes('/api/me')) return '👤';
    if (path.includes('/api/bots')) return '🤖';
    if (path.includes('/api/positions')) return '💰';
    if (path.includes('/api/ticks')) return '📡';
    if (path.includes('/api/access-logs')) return '📋';
    if (path.includes('/api/health')) return '❤️';
    return '🔗';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin - Access Logs</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">⚠️ {error}</p>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0 mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Limit:</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filter by Email:</label>
            <input
              type="text"
              placeholder="Enter email..."
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={fetchAccessLogs}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-blue-600 text-2xl">📊</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-600">Total Logs</p>
                <p className="text-2xl font-bold text-blue-900">{accessLogs.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-green-600 text-2xl">👥</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-600">Unique Users</p>
                <p className="text-2xl font-bold text-green-900">
                  {new Set(accessLogs.map(log => log.user_email)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-purple-600 text-2xl">🌐</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-600">Unique IPs</p>
                <p className="text-2xl font-bold text-purple-900">
                  {new Set(accessLogs.map(log => log.ip)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-orange-600 text-2xl">🕒</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-orange-600">Last Activity</p>
                <p className="text-lg font-bold text-orange-900">
                  {accessLogs.length > 0 
                    ? new Date(accessLogs[0].created_at).toLocaleDateString()
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Access Logs Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.user_email === 'anonymous' ? (
                      <span className="text-gray-500 italic">Anonymous</span>
                    ) : (
                      log.user_email
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {log.ip || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMethodColor(log.method)}`}>
                      {log.method}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <span className="mr-2">{getPathIcon(log.path)}</span>
                      <span className="font-mono">{log.path}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={log.user_agent}>
                    {log.user_agent || 'Unknown'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredLogs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {filterEmail ? 'No access logs found for the specified email filter.' : 'No access logs found.'}
            </div>
          )}
        </div>
      </div>
        </div>
      </div>
    </>
  );
}

export default Admin;







